import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { mdiAlert, mdiArrowRight, mdiCheck, mdiClose, mdiLoading, mdiPlay, mdiPlayCircleOutline, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { usePropertyDefinitionsByCodes, type PropertyDefinition } from '../../hooks/useQueries'
import { getBooleanOptions, getPropertyDefinitionName, getPropertyConstraints, EnumPropertyInput } from '../properties/PropertyInputUtils'
import { EmptyState } from '../ui/empty-state'
import { INPUT_TODAY, inputPropertyCode, parseInput } from './inputs'

// A children.<type>.<code> test value: one value per descendant, as a JSON
// array or comma-separated (numbers coerced).
export function parseChildrenList(text: string): unknown[] {
  const trimmed = text.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // fall through to the comma-separated form
    }
  }
  return trimmed.split(',').map((v) => v.trim()).filter((v) => v !== '').map((v) => (v !== '' && !isNaN(Number(v)) ? Number(v) : v))
}

interface DerivationTestPanelProps {
  org: string
  uid: string
  // Declared input paths: `code`, `parent.<type>.<code>`, `today`.
  inputs: string[]
  testValues: Record<string, any>[]
  onTestValuesChange: (values: Record<string, any>[]) => void
  // Paths the last test run read through ctx.prop without declaring them.
  onUndeclaredInputs?: (paths: string[]) => void
}

export function DerivationTestPanel({ org, uid, inputs, testValues, onTestValuesChange, onUndeclaredInputs }: DerivationTestPanelProps) {
  const { i18n } = useTranslation()
  const { t: tCommon } = useTranslation('common')
  const { t: tDerivations } = useTranslation('derivations')
  // Evaluating is a read; storing test values PATCHes the derivation, so only
  // that half needs `configuration.write` (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')
  const [inputValues, setInputValues] = useState<Record<string, any>>(
    Object.fromEntries(inputs.map(path => [path, '']))
  )
  const [result, setResult] = useState<any>(null)
  const [allResults, setAllResults] = useState<any[] | null>(null)

  // Fetch the definitions of the properties the inputs read, on the asset
  // or on an ancestor.
  const propertyCodes = useMemo(
    () => Array.from(new Set(inputs.map(inputPropertyCode).filter((c): c is string => !!c))),
    [inputs]
  )
  const { data: propDefs = [] } = usePropertyDefinitionsByCodes(org, propertyCodes)

  // Lookup by input path: a parent input reads the same property definition.
  const propDefByCode = useMemo(() => {
    const byCode: Record<string, PropertyDefinition> = {}
    for (const pd of propDefs) {
      if (pd.code) byCode[pd.code] = pd
    }
    const map: Record<string, PropertyDefinition> = {}
    for (const path of inputs) {
      // A children input takes a list: a plain text field, parsed below.
      if (parseInput(path).kind === 'children') continue
      const code = inputPropertyCode(path)
      if (code && byCode[code]) map[path] = byCode[code]
    }
    return map
  }, [propDefs, inputs])

  const testMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}/test', {
    onSuccess: (data: any) => {
      const runs: any[] = data.results ? data.results : [data]
      onUndeclaredInputs?.(Array.from(new Set(runs.flatMap(r => r?.undeclared_inputs || []))).sort())
      if (data.results) {
        setAllResults(data.results)
        setResult(null)
      } else {
        setResult(data)
        setAllResults(null)
      }
    }
  })

  const parseInputValues = useCallback(() => {
    const parsed: Record<string, any> = {}
    for (const [key, val] of Object.entries(inputValues)) {
      if (val === '' || val === undefined || val === null) continue
      if (parseInput(key).kind === 'children') {
        parsed[key] = parseChildrenList(String(val))
        continue
      }
      const propDef = propDefByCode[key]

      if (propDef) {
        switch (propDef.data_type) {
          case 'boolean':
            parsed[key] = val === 'true'
            break
          case 'number':
            if (val !== '') {
              const num = Number(val)
              if (!isNaN(num)) parsed[key] = num
              else parsed[key] = val
            }
            break
          case 'enum':
            if (propDef.properties?.multiple === true && typeof val === 'string' && val) {
              parsed[key] = val.split(',').map((v: string) => v.trim()).filter((v: string) => v)
            } else {
              parsed[key] = val
            }
            break
          case 'date':
            if (val) {
              if (propDef.properties?.with_time === true) {
                const dateValue = new Date(val)
                parsed[key] = dateValue.toISOString()
              } else {
                parsed[key] = val
              }
            }
            break
          default:
            parsed[key] = val
        }
      } else {
        // Fallback: try to detect types
        if (val === 'true') parsed[key] = true
        else if (val === 'false') parsed[key] = false
        else if (!isNaN(Number(val))) parsed[key] = Number(val)
        else if (typeof val === 'string' && val.startsWith('[')) {
          try { parsed[key] = JSON.parse(val) } catch { parsed[key] = val }
        } else {
          parsed[key] = val
        }
      }
    }
    return parsed
  }, [inputValues, propDefByCode])

  const handleEvaluate = useCallback(() => {
    const parsed = parseInputValues()
    testMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { input_values: parsed } as any
    })
  }, [parseInputValues, org, uid, testMutation])

  const handleAddToTestValues = useCallback(() => {
    const parsed = parseInputValues()
    if (Object.keys(parsed).length === 0) return
    onTestValuesChange([...testValues, parsed])
  }, [parseInputValues, testValues, onTestValuesChange])

  const handleRemoveTestValue = useCallback((index: number) => {
    onTestValuesChange(testValues.filter((_, i) => i !== index))
  }, [testValues, onTestValuesChange])

  const loadTestValue = useCallback((tv: Record<string, any>) => {
    const newInputValues: Record<string, any> = {}
    for (const code of inputs) {
      const val = tv[code]
      if (val === undefined || val === null) {
        newInputValues[code] = ''
        continue
      }
      const propDef = propDefByCode[code]
      if (propDef?.data_type === 'boolean') {
        newInputValues[code] = String(val)
      } else if (propDef?.data_type === 'number') {
        newInputValues[code] = String(val)
      } else if (propDef?.data_type === 'date' && propDef.properties?.with_time && typeof val === 'string') {
        newInputValues[code] = val.slice(0, 16)
      } else if (propDef?.data_type === 'enum' && Array.isArray(val)) {
        newInputValues[code] = val.join(',')
      } else if (typeof val === 'object') {
        newInputValues[code] = JSON.stringify(val)
      } else {
        newInputValues[code] = String(val)
      }
    }
    setInputValues(newInputValues)
    // Auto-evaluate with the stored values
    testMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { input_values: tv } as any
    })
  }, [inputs, propDefByCode, testMutation, org, uid])

  const handleTestAll = useCallback(() => {
    testMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { test_all: true } as any
    })
  }, [org, uid, testMutation])

  const summarizeTestValue = (tv: Record<string, any>) => {
    return Object.entries(tv)
      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join(', ')
  }

  const renderInput = (code: string) => {
    const propDef = propDefByCode[code]
    const value = inputValues[code] ?? ''
    const setValue = (v: any) => setInputValues(prev => ({ ...prev, [code]: v }))

    if (code === INPUT_TODAY) {
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid={`test-input-${code}`}
        />
      )
    }

    if (!propDef) {
      // Fallback: generic text input
      return (
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="string, number, true/false, or [array]"
          className="font-mono text-sm"
          data-testid={`test-input-${code}`}
        />
      )
    }

    switch (propDef.data_type) {
      case 'boolean': {
        const semantic = propDef.properties?.semantic as string | undefined
        const options = getBooleanOptions(semantic, tCommon)
        return (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid={`test-input-${code}`}
          >
            <option value="">-- Select --</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )
      }

      case 'enum':
        return (
          <div data-testid={`test-input-${code}`}>
            <EnumPropertyInput
              propertyDefinitionUid={propDef.uid!}
              value={value}
              onChange={setValue}
              org={org}
            />
          </div>
        )

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            step={propDef.properties?.integer_only ? '1' : 'any'}
            min={typeof propDef.properties?.min_value === 'number' ? propDef.properties.min_value : undefined}
            max={typeof propDef.properties?.max_value === 'number' ? propDef.properties.max_value : undefined}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder={`Enter ${propDef.properties?.integer_only ? 'integer' : 'number'}${propDef.properties?.unit ? ` (${propDef.properties.unit})` : ''}...`}
            data-testid={`test-input-${code}`}
          />
        )

      case 'date': {
        const withTime = propDef.properties?.with_time === true
        return (
          <input
            type={withTime ? 'datetime-local' : 'date'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => (e.currentTarget as any).showPicker?.()}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid={`test-input-${code}`}
          />
        )
      }

      case 'text':
      default:
        return (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter text..."
            className="text-sm"
            data-testid={`test-input-${code}`}
          />
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Input Values */}
      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{tDerivations('test.inputs_title')}</h3>
        {inputs.length === 0 ? (
          <EmptyState variant="inline" title={tDerivations('detail.test_no_input_properties')} />
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider w-1/3">
                  {tDerivations('test.property')}
                </th>
                <th className="px-4 py-2 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  {tDerivations('test.value')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {inputs.map(code => {
                const propDef = propDefByCode[code]
                const displayName = code === INPUT_TODAY
                  ? tDerivations('inputs.today')
                  : getPropertyDefinitionName(propDef, inputPropertyCode(code) || code, i18n.language)
                const constraints = getPropertyConstraints(propDef, tCommon)
                return (
                  <tr key={code} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground align-top">
                      <div>
                        <div className="font-medium">{displayName}</div>
                        <div className="text-xs text-muted-foreground font-mono">{code}</div>
                        {constraints.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1 font-mono">
                            {constraints.join(' · ')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {renderInput(code)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={handleEvaluate}
            disabled={testMutation.isPending}
            data-testid="evaluate-btn"
          >
            {testMutation.isPending ? (
              <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
            ) : (
              <AppIcon path={mdiPlay} size="sm" className="mr-2" />
            )}
            {tDerivations('test.evaluate')}
          </Button>
          {canWriteConfiguration && (
            <Button
              variant="outline"
              onClick={handleAddToTestValues}
              disabled={inputs.length === 0}
              data-testid="add-to-test-values-btn"
            >
              <AppIcon path={mdiPlus} size="sm" className="mr-2" />
              {tDerivations('test.add_to_test_values')}
            </Button>
          )}
        </div>
      </div>

      {/* Stored Test Values */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{tDerivations('test.stored_title')}</h3>
          <Button
            onClick={handleTestAll}
            disabled={testMutation.isPending || testValues.length === 0}
            size="sm"
            data-testid="test-all-btn"
          >
            {testMutation.isPending ? (
              <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
            ) : (
              <AppIcon path={mdiPlayCircleOutline} size="sm" className="mr-2" />
            )}
            {tDerivations('test.test_all', { count: testValues.length })}
          </Button>
        </div>

        {testValues.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {testValues.map((tv, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="gap-1 font-mono text-xs max-w-xs truncate cursor-pointer hover:bg-accent"
                onClick={() => loadTestValue(tv)}
                data-testid={`load-test-value-${idx}`}
              >
                {summarizeTestValue(tv)}
                {canWriteConfiguration && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveTestValue(idx) }}
                    className="ml-1 hover:text-destructive shrink-0"
                    data-testid={`remove-test-value-${idx}`}
                  >
                    <AppIcon path={mdiClose} size="xs" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{tDerivations('test.no_stored')}</p>
        )}
      </div>

      {/* Single Result: the output, the decision row that answered and the
          inputs the script read (spec 2026-10-06-13). */}
      {result && (
        <div className="space-y-4" data-testid="test-result">
          <div className={`rounded-lg border p-4 flex items-center gap-3 ${
            result.error
              ? 'border-destructive/50 bg-destructive/10'
              : result.matched
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30'
                : 'border-border bg-muted/40'
          }`}>
            {result.error ? (
              <AppIcon path={mdiAlert} size="md" className="text-destructive shrink-0" />
            ) : result.matched ? (
              <AppIcon path={mdiCheck} size="md" className="text-green-600 dark:text-green-400 shrink-0" />
            ) : (
              <AppIcon path={mdiClose} size="md" className="text-muted-foreground shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              {result.error ? (
                <div data-testid="test-result-error">
                  <div className="font-medium text-foreground">{tDerivations('test.error')}</div>
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs mt-1">{result.error}</pre>
                </div>
              ) : result.matched ? (
                <div>
                  <div className="font-medium text-foreground flex items-center gap-2">
                    {tDerivations('test.output')}
                    <AppIcon path={mdiArrowRight} size="xs" />
                    <code className="bg-background/80 px-2 py-0.5 rounded text-sm" data-testid="test-result-matched">
                      {typeof result.output === 'string' ? result.output : JSON.stringify(result.output)}
                    </code>
                  </div>
                  {result.matched_rule && (
                    <p className="text-sm text-muted-foreground mt-0.5" data-testid="test-result-matched-row">
                      {tDerivations('test.matched_row', { priority: result.matched_rule.priority })}
                      {result.matched_rule.comment ? ` · ${result.matched_rule.comment}` : ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="font-medium text-foreground" data-testid="test-result-no-match">
                  {tDerivations('test.no_answer')}
                </div>
              )}
            </div>
          </div>

          {result.undeclared_inputs?.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm" data-testid="test-result-undeclared-inputs">
              <AppIcon path={mdiAlert} size="sm" className="mt-0.5 flex-shrink-0 text-warning" />
              <span>{tDerivations('test.undeclared_inputs', { paths: result.undeclared_inputs.join(', ') })}</span>
            </div>
          )}

          {result.inputs && Object.keys(result.inputs).length > 0 && (
            <div className="bg-card rounded-lg border border-border">
              <div className="px-5 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{tDerivations('test.inputs_read')}</h3>
              </div>
              <Table data-testid="test-result-inputs">
                <TableBody>
                  {Object.entries(result.inputs as Record<string, any>).map(([path, v]) => (
                    <TableRow key={path}>
                      <TableCell className="font-mono text-xs w-1/3">{path}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {v === null || v === undefined
                          ? <span className="text-muted-foreground italic">{tDerivations('test.absent')}</span>
                          : (typeof v === 'string' ? v : JSON.stringify(v))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Test All Results */}
      {allResults && allResults.length > 0 && (
        <div className="bg-card rounded-lg border border-border">
          <div className="px-5 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {tDerivations('test.results_title', { count: allResults.length })}
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tDerivations('test.inputs_title')}</TableHead>
                <TableHead>{tDerivations('test.output')}</TableHead>
                <TableHead className="w-28">{tDerivations('test.result')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allResults.map((r: any, idx: number) => (
                <TableRow
                  key={idx}
                  className={r.matched ? '' : 'bg-red-50/50 dark:bg-red-950/10'}
                >
                  <TableCell>
                    <code className="text-sm font-mono">{summarizeTestValue(r.input_values || {})}</code>
                  </TableCell>
                  <TableCell>
                    <code className="text-sm font-mono">
                      {r.matched
                        ? (typeof r.output === 'string' ? r.output : JSON.stringify(r.output))
                        : '-'}
                    </code>
                  </TableCell>
                  <TableCell>
                    {r.matched ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 gap-1 text-xs" data-testid={`test-all-result-${idx}`}>
                        <AppIcon path={mdiCheck} size="xs" />
                        {tDerivations('test.answered')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-xs text-muted-foreground" data-testid={`test-all-result-${idx}`}>
                        <AppIcon path={mdiClose} size="xs" />
                        {r.error ? tDerivations('test.error') : tDerivations('test.not_answered')}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
